/**
 * S16 · Sync Queue — top-level sync control centre. Shows rollup
 * counts, a live list of pending tasks, and entry points to the
 * Active (Syncing) and Conflict screens.
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { SyncTask } from '@kapture/types';
import { Btn, Chip, GestureBar, I, Ico, ScreenFrame, StatusBar, T } from '@/components/primitives';
import { useDiscardTaskMutation, useDrainMutation, usePendingSyncTasks, useRetryTaskMutation, useSyncRollup, useSyncStatus } from '@kapture/client-core';

export const Route = createFileRoute('/sync/')({
  component: SyncQueuePage,
  staticData: { mobile: true },
});

function SyncQueuePage() {
  const navigate = useNavigate();
  const { online } = useSyncStatus();
  const { data: rollup } = useSyncRollup(3000);
  const { data: pending } = usePendingSyncTasks(3000);
  const drainMut = useDrainMutation();
  const retry = useRetryTaskMutation();
  const discard = useDiscardTaskMutation();

  const pendingCount = (rollup?.queued ?? 0) + (rollup?.retrying ?? 0);
  const conflicts = rollup?.conflict ?? 0;

  // Named for what it is, not `syncNow` — that shadowed the `syncNow` in
  // lib/sync/triggers, which is push *then* pull. The mutation underneath
  // does call the real one; the problem was ordering. Navigating first
  // unmounted this component mid-flight, so the tap often pushed nothing and
  // pulled nothing.
  async function startSync() {
    const running = drainMut.mutateAsync();
    navigate({ to: '/sync/active' });
    await running;
  }

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: `1px solid ${T.stroke}`,
        }}
      >
        <Link
          to="/"
          aria-label="Back"
          style={{ color: T.text, display: 'flex', textDecoration: 'none' }}
        >
          <Ico d={I.back} size={20} />
        </Link>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, fontFamily: T.font }}>Sync queue</div>
          <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font }}>
            {online ? 'Connected' : 'Offline — tasks will resume when signal returns'}
          </div>
        </div>
        {online ? (
          <Chip tone="good" size="sm">
            Online
          </Chip>
        ) : (
          <Chip tone="warn" size="sm">
            Offline
          </Chip>
        )}
      </div>

      <div
        style={{
          padding: 16,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 10,
        }}
      >
        <RollupCard label="Queued" value={rollup?.queued ?? 0} tone={T.brand} />
        <RollupCard label="Conflict" value={rollup?.conflict ?? 0} tone={T.warn} />
        <RollupCard label="Succeeded" value={rollup?.succeeded ?? 0} tone={T.good} />
        <RollupCard label="Failed" value={rollup?.failed ?? 0} tone={T.danger} />
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 16px' }}>
        {conflicts > 0 && <ConflictBanner count={conflicts} />}

        <div
          style={{
            fontSize: 10,
            color: T.textMute,
            fontFamily: T.mono,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginTop: 14,
            marginBottom: 8,
          }}
        >
          {pendingCount} pending · {pending?.length ?? 0} in view
        </div>

        {(pending ?? []).length === 0 && (
          <EmptyState online={online} onSync={() => void startSync()} />
        )}
        {(pending ?? []).map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            onRetry={() => retry.mutate(task.id)}
            onDiscard={() => discard.mutate(task.id)}
          />
        ))}
      </div>

      <div
        style={{
          padding: '10px 16px 12px',
          borderTop: `1px solid ${T.stroke}`,
          display: 'flex',
          gap: 8,
        }}
      >
        <Btn
          tone="light"
          size="md"
          icon={<Ico d={I.refresh} size={14} />}
          onClick={() => void startSync()}
          disabled={!online || pendingCount === 0}
          style={{ opacity: online ? 1 : 0.5 }}
        >
          Retry all
        </Btn>
        <Btn
          tone="primary"
          size="md"
          full
          iconRight={<Ico d={I.arrow} size={16} />}
          onClick={() => void startSync()}
          disabled={!online || drainMut.isPending}
          style={{ opacity: online ? 1 : 0.5 }}
        >
          {drainMut.isPending ? 'Syncing…' : 'Sync now'}
        </Btn>
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

function RollupCard({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div
      style={{
        padding: '14px 14px 12px',
        borderRadius: 12,
        border: `1px solid ${T.stroke}`,
        background: T.surface,
        boxShadow: T.sh,
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: T.textMute,
          fontFamily: T.mono,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 26,
          fontWeight: 700,
          fontFamily: T.font,
          color: value > 0 ? tone : T.textDim,
          letterSpacing: '-0.02em',
          marginTop: 6,
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function ConflictBanner({ count }: { count: number }) {
  return (
    <Link
      to="/sync/conflicts/$taskId"
      params={{ taskId: 'first' }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 12,
        background: T.warnSoft,
        border: `1px solid ${T.warn}`,
        borderRadius: 12,
        color: 'inherit',
        textDecoration: 'none',
        marginTop: 8,
      }}
    >
      <Ico d={I.warn} size={16} stroke={T.warnText} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.warnText, fontFamily: T.font }}>
          {count} {count === 1 ? 'conflict needs' : 'conflicts need'} review
        </div>
        <div style={{ fontSize: 11, color: T.warnText, opacity: 0.85, fontFamily: T.font }}>
          A teammate edited a field you also changed. Tap to merge.
        </div>
      </div>
      <Ico d={I.chev} size={16} stroke={T.warnText} />
    </Link>
  );
}

function TaskRow({
  task,
  onRetry,
  onDiscard,
}: {
  task: SyncTask;
  onRetry(): void;
  onDiscard(): void;
}) {
  return (
    <div
      style={{
        padding: '12px 14px',
        background: T.surface,
        border: `1px solid ${T.stroke}`,
        borderRadius: 12,
        marginBottom: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          background: opBg(task),
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Ico d={opIcon(task)} size={15} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            fontFamily: T.font,
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {opLabel(task)}
        </div>
        <div style={{ fontSize: 10, color: T.textMute, fontFamily: T.mono, marginTop: 2 }}>
          seq {task.seq} · {task.attempts} attempt{task.attempts === 1 ? '' : 's'}
          {task.byteSize > 0 ? ` · ${formatBytes(task.byteSize)}` : ''}
        </div>
      </div>
      <StatusPill status={task.status} />
      {task.status === 'failed' || task.status === 'retrying' ? (
        <button type="button" onClick={onRetry} aria-label="Retry" style={iconBtn}>
          <Ico d={I.refresh} size={14} />
        </button>
      ) : null}
      {task.status === 'failed' ? (
        <button
          type="button"
          onClick={onDiscard}
          aria-label="Discard task"
          style={{ ...iconBtn, color: T.danger }}
        >
          <Ico d={I.trash} size={14} />
        </button>
      ) : null}
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 8,
  border: `1px solid ${T.stroke}`,
  background: T.surface,
  color: T.textDim,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};

function StatusPill({ status }: { status: SyncTask['status'] }) {
  const tone =
    status === 'succeeded'
      ? 'good'
      : status === 'queued'
        ? 'neutral'
        : status === 'uploading'
          ? 'info'
          : status === 'conflict'
            ? 'warn'
            : status === 'failed'
              ? 'danger'
              : status === 'retrying'
                ? 'warn'
                : 'neutral';
  return (
    <Chip tone={tone} size="sm">
      {status}
    </Chip>
  );
}

function EmptyState({ online, onSync }: { online: boolean; onSync: () => void }) {
  return (
    <div
      style={{
        padding: '40px 12px',
        textAlign: 'center',
        color: T.textDim,
        fontFamily: T.font,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 6 }}>
        Queue is empty
      </div>
      {online
        ? "You're all caught up. Future changes will queue here until they land."
        : "Nothing waiting — you'll see tasks here when you capture offline."}
      <div style={{ marginTop: 16 }}>
        <Btn
          tone="light"
          size="sm"
          onClick={onSync}
          disabled={!online}
          style={{ opacity: online ? 1 : 0.5 }}
        >
          Force a drain
        </Btn>
      </div>
    </div>
  );
}

function opIcon(task: SyncTask): string | React.ReactNode {
  switch (task.op) {
    case 'media.upload':
    case 'signature.upload':
      return I.image;
    case 'capture.upsert':
    case 'capture.delete':
      return I.camera;
    case 'session.upsert':
    case 'session.submit':
      return I.folder;
    case 'fieldValue.upsert':
      return I.file;
  }
}

function opBg(task: SyncTask): string {
  if (task.status === 'conflict') return T.warn;
  if (task.status === 'failed') return T.danger;
  if (task.status === 'succeeded') return T.good;
  return T.ink;
}

function opLabel(task: SyncTask): string {
  switch (task.op) {
    case 'media.upload':
      return `Upload media · ${task.entityId.slice(0, 8)}`;
    case 'signature.upload':
      return `Upload signature · ${task.entityId.slice(0, 8)}`;
    case 'capture.upsert':
      return `Capture upsert · ${task.entityId.slice(0, 8)}`;
    case 'capture.delete':
      return `Capture delete · ${task.entityId.slice(0, 8)}`;
    case 'session.upsert':
      return `Session upsert · ${task.entityId.slice(0, 8)}`;
    case 'session.submit':
      return `Submit session · ${task.entityId.slice(0, 8)}`;
    case 'fieldValue.upsert':
      return `Field value · ${task.entityId.slice(0, 8)}`;
  }
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
